// FormBuilderLayoutSection — the "Layout & styling" body inside Advanced
// options, per the design: Background-color preset pills (Default keeps the
// type's own default · Transparent · four pastel presets), label-above Selects
// for the per-type enums (answer format, text lines, number type, map type,
// accuracy), a signature-color swatch, the Field-layout segmented
// control, Keyboard style, and toggle cards (collapse-on-select / without-
// frame). Rows are driven by which keys the selected type's layoutProperties
// defaults carry (same gating as the validation section).

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, SelectDropdown, SegmentedControl, Switch } from '@/components/common';
import { formElementDefaultProperties, type ElementProperties } from './utils/formElementDefaults';
import type { BuilderField, BuilderPage } from './utils/builderModel';

// Background presets (confirmed FIELD_COLORS hexes, stored in the model's
// 8-digit idiom — matching compares the rgb part so 6-digit legacy values
// light the right pill).
const BG_PRESETS = [
  { key: 'amber', hex: '#fef3c7ff' },
  { key: 'blue', hex: '#dbeafeff' },
  { key: 'green', hex: '#dcfce7ff' },
  { key: 'pink', hex: '#fce7f3ff' },
];

/** Case/alpha-insensitive color comparison key (#RRGGBB[AA] → #rrggbb). */
const rgbKey = (hex: string) => (hex ?? '').toLowerCase().slice(0, 7);

// Confirmed value sets; labels use this repo's key pattern.
const SELECT_ROWS: {
  key: string;
  labelKey: string;
  options: { value: string; labelKey?: string }[];
}[] = [
  {
    key: 'answerFormat',
    labelKey: 'forms_builder_props_lay_answer_format',
    options: [
      { value: 'description', labelKey: 'forms_builder_props_lay_af_description' },
      { value: 'email', labelKey: 'forms_builder_props_lay_af_email' },
      { value: 'localNumber', labelKey: 'forms_builder_props_lay_af_local' },
      { value: 'internationalNumber', labelKey: 'forms_builder_props_lay_af_international' },
      { value: 'webAddress', labelKey: 'forms_builder_props_lay_af_web' },
    ],
  },
  {
    key: 'textLines',
    labelKey: 'forms_builder_props_lay_text_lines',
    options: [
      { value: 'single', labelKey: 'forms_builder_props_lay_tl_single' },
      { value: 'multiple', labelKey: 'forms_builder_props_lay_tl_multiple' },
    ],
  },
  {
    key: 'numberType',
    labelKey: 'forms_builder_props_lay_number_type',
    options: [
      { value: 'integer', labelKey: 'forms_builder_props_lay_nt_integer' },
      { value: 'decimal', labelKey: 'forms_builder_props_lay_nt_decimal' },
    ],
  },
  {
    key: 'mapType',
    labelKey: 'forms_builder_props_lay_map_type',
    options: [
      { value: 'standard', labelKey: 'forms_builder_props_lay_mt_standard' },
      { value: 'satellite', labelKey: 'forms_builder_props_lay_mt_satellite' },
      { value: 'hybrid', labelKey: 'forms_builder_props_lay_mt_hybrid' },
    ],
  },
  {
    key: 'accuracy',
    labelKey: 'forms_builder_props_lay_accuracy',
    options: ['5', '10', '25', '50', '100'].map((value) => ({ value })),
  },
];

// #rrggbbaa → #rrggbb for color inputs / swatch fills.
const rgb6 = (hex: string) => {
  const h = hex?.startsWith('#') ? hex.slice(1) : '';
  return h.length >= 6 ? `#${h.slice(0, 6)}` : '#ffffff';
};

interface FormBuilderLayoutSectionProps {
  field: BuilderField | BuilderPage;
  onChange: (properties: ElementProperties) => void;
}

const FormBuilderLayoutSection = ({ field, onChange }: FormBuilderLayoutSectionProps) => {
  const { t } = useTranslation();

  const properties = field.properties;
  const defaults = formElementDefaultProperties[field.elementType];
  if (!('layoutProperties' in properties) || !('layoutProperties' in defaults)) return null;
  const lp = properties.layoutProperties as Record<string, unknown>;
  const typeDefaults = defaults.layoutProperties as Record<string, unknown>;

  const setLayout = (patch: Record<string, unknown>) =>
    onChange({
      ...properties,
      layoutProperties: { ...(properties.layoutProperties as object), ...patch },
    } as ElementProperties);

  // Background selection: Transparent wins; else Default when the color equals
  // the type's own default; else a matching preset (custom values → none).
  const bgDefault = typeDefaults.backgroundColor as string;
  const transparent = lp.transparent === true;
  const bg = (lp.backgroundColor as string) || bgDefault;
  const activeBg = transparent
    ? 'transparent'
    : rgbKey(bg) === rgbKey(bgDefault)
      ? 'default'
      : (BG_PRESETS.find((p) => rgbKey(p.hex) === rgbKey(bg))?.key ?? null);

  const bgPill = (key: string, onClick: () => void, swatch: ReactNode) => (
    <button
      key={key}
      type='button'
      className={[
        'form-builder-props__bg-pill',
        activeBg === key && 'form-builder-props__bg-pill--active',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-pressed={activeBg === key}
      onClick={onClick}
    >
      {swatch}
      {t(`forms_builder_props_lay_bg_${key}`)}
    </button>
  );

  const selectRow = (row: (typeof SELECT_ROWS)[number]) => (
    <div key={row.key} className='form-builder-props__lay-row'>
      <span className='form-builder-props__label'>{t(row.labelKey)}</span>
      <SelectDropdown
        size='sm'
        aria-label={t(row.labelKey)}
        value={String(lp[row.key] ?? '')}
        options={row.options.map((o) => ({
          value: o.value,
          label: o.labelKey ? t(o.labelKey) : o.value,
        }))}
        onChange={(next) => setLayout({ [row.key]: next })}
      />
    </div>
  );

  const toggleCard = (key: string, icon: string) =>
    key in typeDefaults && (
      <div className='form-builder-props__toggle-card'>
        <span className='form-builder-props__toggle-icon'>
          <Icon name={icon} size={16} />
        </span>
        <span className='form-builder-props__toggle-text'>
          <span className='form-builder-props__toggle-title'>
            {t(`forms_builder_props_lay_${key}`)}
          </span>
          <span className='form-builder-props__toggle-desc'>
            {t(`forms_builder_props_lay_${key}_desc`)}
          </span>
        </span>
        <Switch
          checked={lp[key] === true}
          aria-label={t(`forms_builder_props_lay_${key}`)}
          onChange={(e) => setLayout({ [key]: e.target.checked })}
          size='sm'
        />
      </div>
    );

  return (
    <div className='form-builder-props__lay'>
      {'backgroundColor' in typeDefaults && (
        <div className='form-builder-props__lay-row'>
          <span className='form-builder-props__label'>
            {t('forms_builder_props_lay_bg')}
            <span className='form-builder-props__label-note'>
              {' · '}
              {t('forms_builder_props_lay_bg_note')}
            </span>
          </span>
          <div className='form-builder-props__bg-grid'>
            {bgPill(
              'default',
              () => setLayout({ backgroundColor: bgDefault, transparent: false }),
              <span
                className='form-builder-props__bg-swatch form-builder-props__bg-swatch--default'
                style={{ background: rgb6(bgDefault) }}
                aria-hidden='true'
              />,
            )}
            {'transparent' in typeDefaults &&
              bgPill(
                'transparent',
                () => setLayout({ transparent: true }),
                <span
                  className='form-builder-props__bg-swatch form-builder-props__bg-swatch--transparent'
                  aria-hidden='true'
                />,
              )}
            {BG_PRESETS.map((p) =>
              bgPill(
                p.key,
                () => setLayout({ backgroundColor: p.hex, transparent: false }),
                <span
                  className='form-builder-props__bg-swatch'
                  style={{ background: rgb6(p.hex) }}
                  aria-hidden='true'
                />,
              ),
            )}
          </div>
        </div>
      )}

      {SELECT_ROWS.filter((row) => row.key in typeDefaults).map(selectRow)}

      {'signatureColor' in typeDefaults && (
        <div className='form-builder-props__lay-row'>
          <span className='form-builder-props__label'>
            {t('forms_builder_props_lay_signature_color')}
          </span>
          <label className='form-builder-props__swatch'>
            <input
              type='color'
              value={rgb6((lp.signatureColor as string) || '#000000ff')}
              aria-label={t('forms_builder_props_lay_signature_color')}
              onChange={(e) => setLayout({ signatureColor: `${e.target.value}ff` })}
            />
            <span
              className='form-builder-props__swatch-chip'
              style={{ background: rgb6((lp.signatureColor as string) || '#000000ff') }}
              aria-hidden='true'
            />
            {rgb6((lp.signatureColor as string) || '#000000ff')}
          </label>
        </div>
      )}

      {'fieldLayout' in typeDefaults && (
        <div className='form-builder-props__lay-row'>
          <span className='form-builder-props__label'>
            {t('forms_builder_props_lay_field_layout')}
          </span>
          <SegmentedControl
            aria-label={t('forms_builder_props_lay_field_layout')}
            value={String(lp.fieldLayout ?? 'horizontal')}
            onChange={(v) => setLayout({ fieldLayout: v })}
            options={[
              {
                value: 'horizontal',
                label: t('forms_builder_props_lay_fl_horizontal'),
                icon: 'panel-left',
              },
              {
                value: 'vertical',
                label: t('forms_builder_props_lay_fl_vertical'),
                icon: 'panel-top',
              },
            ]}
          />
        </div>
      )}

      {'keyboardStyle' in typeDefaults && (
        <div className='form-builder-props__lay-row'>
          <span className='form-builder-props__label'>
            {t('forms_builder_props_lay_keyboard_style')}
          </span>
          {/* <string> explicitly: the inline literals would narrow V to
              'keyboard' | 'keypad', which the String()-coerced value
              (layoutProperties is untyped) is not assignable to. */}
          <SelectDropdown<string>
            size='sm'
            aria-label={t('forms_builder_props_lay_keyboard_style')}
            value={String(lp.keyboardStyle ?? 'keyboard')}
            options={[
              { value: 'keyboard', label: t('forms_builder_props_lay_ks_keyboard') },
              { value: 'keypad', label: t('forms_builder_props_lay_ks_keypad') },
            ]}
            onChange={(next) => setLayout({ keyboardStyle: next })}
          />
        </div>
      )}

      {toggleCard('collapseOnSelect', 'chevrons-down-up')}
      {toggleCard('withoutFrame', 'frame')}
    </div>
  );
};

export default FormBuilderLayoutSection;
